import type { Vec3Tuple } from './math-types';

export type CsvKind = 'camera' | 'vehicle' | 'ambiguous';
export type CameraCsvFormat = 'posTarget' | 'cogEuler';

export type CameraMotionAnglesDeg = {
    pitch: number | null;
    roll: number | null;
    yaw: number | null;
};

export type CameraMotionCsvSample = {
    /** Row number in the original CSV (1-based, as displayed in spreadsheet editors). */
    sourceRow: number;

    /** Raw time value as read from the CSV (seconds). */
    tRawSec: number;

    /** Time value after optional normalization (seconds). */
    tSec: number;

    position: Vec3Tuple | null;
    target: Vec3Tuple | null;
    anglesDeg: CameraMotionAnglesDeg;
};

export type ParsedCameraMotionCsv = {
    format: CameraCsvFormat | null;
    header: {
        columns: string[];
        index: Map<string, number>;
    };
    samples: CameraMotionCsvSample[];
    warnings: string[];
};

export type ParseCameraMotionCsvOptions = {
    /**
     * When true, subtracts the first sample's time from all samples.
     * This makes the first sample start at t=0.
     */
    zeroTimeToFirstSample?: boolean;
};

const normalizeHeader = (value: string) => {
    const raw = value.replace(/^\uFEFF/, '').trim().toLowerCase();
    if (!raw) return '';

    let key = raw
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '');

    if (key === 'time' || key === 'timestamp' || key === 'seconds' || key === 'second' || key === 'sec') {
        key = 't';
    }

    if (key === 'cogx') key = 'cog_x';
    if (key === 'cogy') key = 'cog_y';
    if (key === 'cogz') key = 'cog_z';

    if (key === 'camx') key = 'cam_x';
    if (key === 'camy') key = 'cam_y';
    if (key === 'camz') key = 'cam_z';

    if (key === 'posx') key = 'pos_x';
    if (key === 'posy') key = 'pos_y';
    if (key === 'posz') key = 'pos_z';

    if (key === 'tgtx') key = 'tgt_x';
    if (key === 'tgty') key = 'tgt_y';
    if (key === 'tgtz') key = 'tgt_z';

    return key;
};

type ParsedCsvLine = {
    cells: string[];
    unterminatedQuote: boolean;
};

const parseCsvLine = (line: string): ParsedCsvLine => {
    const out: string[] = [];
    let cur = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
        const ch = line[i];

        if (inQuotes) {
            if (ch === '"') {
                const next = line[i + 1];
                if (next === '"') {
                    cur += '"';
                    i++;
                } else {
                    inQuotes = false;
                }
            } else {
                cur += ch;
            }
            continue;
        }

        if (ch === ',') {
            out.push(cur);
            cur = '';
            continue;
        }

        if (ch === '"' && cur.trim() === '') {
            cur = '';
            inQuotes = true;
            continue;
        }

        cur += ch;
    }

    out.push(cur);
    return { cells: out, unterminatedQuote: inQuotes };
};

const parseCsv = (text: string): { rows: string[][]; warnings: string[] } => {
    const lines = String(text ?? '').split(/\r?\n/);
    const rows: string[][] = [];
    const warnings: string[] = [];

    for (let i = 0; i < lines.length; i++) {
        const parsed = parseCsvLine(lines[i]);
        rows.push(parsed.cells);
        if (parsed.unterminatedQuote) {
            warnings.push(`Row ${i + 1}: unterminated quote in CSV line.`);
        }
    }

    return { rows, warnings };
};

const finiteNumberRegex = /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:e[+-]?\d+)?$/i;

const parseFiniteNumber = (raw: unknown): number | null => {
    const trimmed = String(raw ?? '').trim();
    if (!trimmed) return null;
    if (!finiteNumberRegex.test(trimmed)) return null;
    const value = Number(trimmed);
    return Number.isFinite(value) ? value : null;
};

const buildHeaderIndex = (headerRow: string[]) => {
    const columns = headerRow.map(c => c.replace(/^\uFEFF/, '').trim());
    const index = new Map<string, number>();

    for (let i = 0; i < columns.length; i++) {
        const key = normalizeHeader(columns[i]);
        if (!key) continue;
        if (!index.has(key)) index.set(key, i);
    }

    return { columns, index };
};

const isRowEmpty = (row: string[]) => {
    for (const cell of row) {
        if (cell.trim() !== '') return false;
    }
    return true;
};

const safeGet = (row: string[], idx: number | undefined): string => {
    if (idx === undefined) return '';
    return row[idx] ?? '';
};

const hasAnyWithPrefix = (keys: Iterable<string>, prefix: string) => {
    for (const key of keys) {
        if (key.startsWith(prefix)) return true;
    }
    return false;
};

const getFirstIndex = (index: Map<string, number>, keys: string[]) => {
    for (const key of keys) {
        const v = index.get(key);
        if (v !== undefined) return v;
    }
    return undefined;
};

const parseVec3 = (row: string[], ix: number | undefined, iy: number | undefined, iz: number | undefined): Vec3Tuple | null => {
    const x = parseFiniteNumber(safeGet(row, ix));
    const y = parseFiniteNumber(safeGet(row, iy));
    const z = parseFiniteNumber(safeGet(row, iz));
    return (x !== null && y !== null && z !== null) ? [x, y, z] : null;
};

const rowLooksHeaderLike = (row: string[]) => {
    for (const cell of row) {
        const key = normalizeHeader(cell);
        if (/[a-z_]/.test(key)) return true;
    }
    return false;
};

export function detectCsvKind(text: string): CsvKind {
    const lines = String(text ?? '').split(/\r?\n/).slice(0, 20);
    const headerLikeRows: string[][] = [];

    for (const line of lines) {
        const { cells } = parseCsvLine(line);
        if (!cells.length || isRowEmpty(cells)) continue;
        if (!rowLooksHeaderLike(cells)) continue;
        headerLikeRows.push(cells);
    }

    const keys = new Set<string>();
    for (const row of headerLikeRows) {
        for (const cell of row) {
            const key = normalizeHeader(cell);
            if (key) keys.add(key);
        }
    }

    if (hasAnyWithPrefix(keys, 'tyre_')) {
        return 'vehicle';
    }

    if (hasAnyWithPrefix(keys, 'tgt_')) {
        return 'camera';
    }

    if (hasAnyWithPrefix(keys, 'cog_') || hasAnyWithPrefix(keys, 'angle_')) {
        return 'vehicle';
    }

    if (hasAnyWithPrefix(keys, 'pos_') || hasAnyWithPrefix(keys, 'cam_')) {
        return 'ambiguous';
    }

    return 'ambiguous';
}

export function parseCameraMotionCsv(text: string, options: ParseCameraMotionCsvOptions = {}): ParsedCameraMotionCsv {
    const warnings: string[] = [];
    const parsedCsv = parseCsv(text);
    warnings.push(...parsedCsv.warnings);

    const rows = parsedCsv.rows;
    type Candidate = { rowIndex: number; format: CameraCsvFormat; startsWithT: boolean };
    const candidates: Candidate[] = [];

    for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        if (!row.length || isRowEmpty(row)) continue;

        const header = buildHeaderIndex(row);
        const hasT = header.index.has('t');
        if (!hasT) continue;

        const hasPos = header.index.has('pos_x') || header.index.has('cam_x');
        const hasPosY = header.index.has('pos_y') || header.index.has('cam_y');
        const hasPosZ = header.index.has('pos_z') || header.index.has('cam_z');
        const hasTgt = header.index.has('tgt_x') && header.index.has('tgt_y') && header.index.has('tgt_z');

        const hasCog = header.index.has('cog_x') && header.index.has('cog_y') && header.index.has('cog_z');
        const hasAngle = header.index.has('angle_pitch') && header.index.has('angle_roll') && header.index.has('angle_yaw');

        if ((hasPos && hasPosY && hasPosZ) && hasTgt) {
            candidates.push({ rowIndex: i, format: 'posTarget', startsWithT: normalizeHeader(row[0] ?? '') === 't' });
        }

        if (hasCog && hasAngle) {
            candidates.push({ rowIndex: i, format: 'cogEuler', startsWithT: normalizeHeader(row[0] ?? '') === 't' });
        }
    }

    const pickCandidate = (format: CameraCsvFormat): Candidate | undefined => {
        const group = candidates.filter(c => c.format === format);
        return group.find(c => c.startsWithT) ?? group[0];
    };

    // Prefer posTarget when both match.
    const selected = pickCandidate('posTarget') ?? pickCandidate('cogEuler');

    if (!selected) {
        return {
            format: null,
            header: { columns: [], index: new Map() },
            samples: [],
            warnings: ['Could not find camera CSV header row (expected pos/target or cog/euler columns).']
        };
    }

    const format = selected.format;
    const headerRowIndex = selected.rowIndex;
    const header = buildHeaderIndex(rows[headerRowIndex]);

    const idxT = header.index.get('t');
    if (idxT === undefined) {
        warnings.push('Missing required column "t".');
        return { format, header, samples: [], warnings };
    }

    const idxPosX = getFirstIndex(header.index, ['pos_x', 'cam_x']);
    const idxPosY = getFirstIndex(header.index, ['pos_y', 'cam_y']);
    const idxPosZ = getFirstIndex(header.index, ['pos_z', 'cam_z']);

    const idxTgtX = header.index.get('tgt_x');
    const idxTgtY = header.index.get('tgt_y');
    const idxTgtZ = header.index.get('tgt_z');

    const idxCogX = header.index.get('cog_x');
    const idxCogY = header.index.get('cog_y');
    const idxCogZ = header.index.get('cog_z');

    const idxPitch = header.index.get('angle_pitch');
    const idxRoll = header.index.get('angle_roll');
    const idxYaw = header.index.get('angle_yaw');

    if (format === 'posTarget') {
        if (idxPosX === undefined || idxPosY === undefined || idxPosZ === undefined) {
            warnings.push('Missing one or more required position columns: POS_X/Y/Z (or CAM_X/Y/Z).');
        }
        if (idxTgtX === undefined || idxTgtY === undefined || idxTgtZ === undefined) {
            warnings.push('Missing one or more required target columns: TGT_X/Y/Z.');
        }
    } else {
        if (idxCogX === undefined || idxCogY === undefined || idxCogZ === undefined) {
            warnings.push('Missing one or more required position columns: COG_X/Y/Z.');
        }
        if (idxPitch === undefined || idxRoll === undefined || idxYaw === undefined) {
            warnings.push('Missing one or more required angle columns: ANGLE_PITCH/ROLL/YAW.');
        }
    }

    const samples: CameraMotionCsvSample[] = [];
    let lastTRaw: number | null = null;

    for (let i = headerRowIndex + 1; i < rows.length; i++) {
        const row = rows[i];
        if (!row.length || isRowEmpty(row)) continue;

        const tRawSec = parseFiniteNumber(safeGet(row, idxT));
        if (tRawSec === null) {
            warnings.push(`Row ${i + 1}: could not parse time (t).`);
            continue;
        }

        if (lastTRaw !== null) {
            if (tRawSec < lastTRaw) {
                warnings.push(`Row ${i + 1}: time went backwards (${tRawSec} < ${lastTRaw}).`);
            } else if (tRawSec === lastTRaw) {
                warnings.push(`Row ${i + 1}: duplicate time value (${tRawSec}).`);
            }
        }
        lastTRaw = tRawSec;

        const position = (format === 'posTarget')
            ? parseVec3(row, idxPosX, idxPosY, idxPosZ)
            : parseVec3(row, idxCogX, idxCogY, idxCogZ);

        const target = (format === 'posTarget')
            ? parseVec3(row, idxTgtX, idxTgtY, idxTgtZ)
            : null;

        const anglesDeg: CameraMotionAnglesDeg = {
            pitch: format === 'cogEuler' ? parseFiniteNumber(safeGet(row, idxPitch)) : null,
            roll: format === 'cogEuler' ? parseFiniteNumber(safeGet(row, idxRoll)) : null,
            yaw: format === 'cogEuler' ? parseFiniteNumber(safeGet(row, idxYaw)) : null
        };

        samples.push({
            sourceRow: i + 1,
            tRawSec,
            tSec: tRawSec,
            position,
            target,
            anglesDeg
        });
    }

    const zeroTimeToFirstSample = options.zeroTimeToFirstSample ?? true;
    if (zeroTimeToFirstSample && samples.length > 0) {
        const t0 = samples[0].tRawSec;
        for (const sample of samples) {
            sample.tSec = sample.tRawSec - t0;
        }
    }

    if (samples.length === 0) {
        warnings.push('No data samples found after header row.');
    }

    return {
        format,
        header,
        samples,
        warnings
    };
}
